import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { PairingService } from "../services/pairingService";
import { aiPairingService } from "../services/aiPairingService";
import "./PairingExplorer.css";

/**
 * PairingExplorer Component - Phase 2.2 Implementation
 *
 * Interactive discovery interface for finding perfect beverage pairings
 * based on food items, occasions, and dietary preferences.
 */
const PairingExplorer = ({
  restaurantId,
  beverages = [],
  menuItems = [],
  onPairingFound = null,
}) => {
  const [searchMode, setSearchMode] = useState("food"); // 'food', 'occasion', 'dietary'
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOccasion, setSelectedOccasion] = useState("");
  const [dietaryRestrictions, setDietaryRestrictions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pairingService = useMemo(() => new PairingService(), []);

  const findPairingsByOccasion = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const occasionSuggestions =
        await aiPairingService.getOccasionRecommendations(
          restaurantId,
          selectedOccasion,
          {
            maxSuggestions: 6,
            includeFoodSuggestions: true,
            preferenceProfile: dietaryRestrictions,
          }
        );

      setSuggestions(
        occasionSuggestions.map((suggestion) => ({
          ...suggestion,
          isOccasionBased: true,
        }))
      );
    } catch (err) {
      console.error("Error finding occasion-based pairings:", err);
      setError("Failed to find pairings for this occasion");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, selectedOccasion, dietaryRestrictions]);

  const findPairingsByDietary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const dietarySuggestions =
        await aiPairingService.getDietaryRecommendations(
          restaurantId,
          dietaryRestrictions,
          {
            maxSuggestions: 6,
            includePairingReasons: true,
          }
        );

      setSuggestions(
        dietarySuggestions.map((suggestion) => ({
          ...suggestion,
          isDietaryBased: true,
        }))
      );
    } catch (err) {
      console.error("Error finding dietary-based pairings:", err);
      setError("Failed to find pairings for dietary preferences");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, dietaryRestrictions]);

  const occasions = [
    "Romantic Dinner",
    "Business Meeting",
    "Celebration",
    "Casual Dining",
    "Happy Hour",
    "Brunch",
    "Dinner Party",
    "Date Night",
    "Family Gathering",
    "Special Occasion",
  ];

  const dietaryOptions = [
    "Vegetarian",
    "Vegan",
    "Gluten-Free",
    "Dairy-Free",
    "Keto",
    "Low-Carb",
    "Pescatarian",
    "Halal",
    "Kosher",
    "Nut-Free",
  ];

  const foodCategories = [
    "Appetizers",
    "Seafood",
    "Meat & Poultry",
    "Pasta",
    "Salads",
    "Desserts",
    "Cheese",
    "Vegetarian",
    "Spicy Foods",
    "BBQ & Grilled",
  ];

  useEffect(() => {
    if (searchMode === "occasion" && selectedOccasion) {
      findPairingsByOccasion();
    }
  }, [selectedOccasion, searchMode, findPairingsByOccasion]);

  useEffect(() => {
    if (searchMode === "dietary" && dietaryRestrictions.length > 0) {
      findPairingsByDietary();
    }
  }, [dietaryRestrictions, searchMode, findPairingsByDietary]);

  const handleFoodSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      setError(null);

      // If it's a menu item, use that directly
      let foodItem = menuItems.find((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      );

      if (!foodItem) {
        // Create a generic food item for search
        foodItem = {
          name: searchQuery,
          category: "Custom",
          description: `Custom food search: ${searchQuery}`,
        };
      }

      await findPairingsForFood(foodItem);
    } catch (err) {
      console.error("Error searching for food pairings:", err);
      setError("Failed to find pairings for this food item");
    } finally {
      setLoading(false);
    }
  };

  const findPairingsForFood = async (foodItem) => {
    try {
      setLoading(true);

      // Get AI-powered pairing suggestions
      const aiSuggestions = await aiPairingService.findBeveragesByFood(
        restaurantId,
        foodItem,
        {
          maxSuggestions: 8,
          confidenceThreshold: 0.5,
          includeProfessionalNotes: true,
        }
      );

      // Enhance with science-based analysis
      const enhancedSuggestions = await Promise.all(
        aiSuggestions.map(async (suggestion) => {
          const beverage =
            beverages.find((b) => b.id === suggestion.beverageId) ||
            suggestion.beverage;

          if (beverage) {
            const analysis = pairingService.analyzePairing(
              beverage,
              foodItem,
              {},
              { occasion: selectedOccasion || "dinner" }
            );

            return {
              ...suggestion,
              beverage,
              scienceScore: analysis.score,
              scienceExplanation: analysis.explanation,
              factors: analysis.factors,
              colorClass: analysis.colorClass,
              combinedScore:
                suggestion.confidence * 0.6 + analysis.score * 0.004, // Normalize science score
            };
          }
          return suggestion;
        })
      );

      // Sort by combined score
      const sortedSuggestions = enhancedSuggestions
        .filter((s) => s.beverage)
        .sort((a, b) => b.combinedScore - a.combinedScore);

      setSuggestions(sortedSuggestions);
    } catch (err) {
      console.error("Error finding pairings for food:", err);
      setError("Failed to generate pairing suggestions");
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    if (onPairingFound) {
      onPairingFound(suggestion);
    }
  };

  const toggleDietaryRestriction = (restriction) => {
    setDietaryRestrictions((prev) =>
      prev.includes(restriction)
        ? prev.filter((r) => r !== restriction)
        : [...prev, restriction]
    );
  };

  const renderSearchInterface = () => {
    switch (searchMode) {
      case "food":
        return (
          <div className="food-search">
            <div className="search-input-group">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter a food item (e.g., grilled salmon, chocolate cake)"
                className="search-input"
                onKeyPress={(e) => e.key === "Enter" && handleFoodSearch()}
              />
              <button
                onClick={handleFoodSearch}
                className="search-button"
                disabled={!searchQuery.trim() || loading}
              >
                🔍 Find Pairings
              </button>
            </div>

            <div className="food-categories">
              <p className="category-label">Popular Categories:</p>
              <div className="category-tags">
                {foodCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSearchQuery(category)}
                    className="category-tag"
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {menuItems.length > 0 && (
              <div className="menu-items-quick-select">
                <p className="category-label">From Your Menu:</p>
                <div className="menu-items-grid">
                  {menuItems.slice(0, 6).map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setSearchQuery(item.name);
                        findPairingsForFood(item);
                      }}
                      className="menu-item-card"
                    >
                      <span className="menu-item-name">{item.name}</span>
                      {item.category && (
                        <span className="menu-item-category">
                          {item.category}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case "occasion":
        return (
          <div className="occasion-search">
            <p className="search-description">
              Find beverages perfect for your dining occasion
            </p>
            <div className="occasions-grid">
              {occasions.map((occasion) => (
                <button
                  key={occasion}
                  onClick={() => setSelectedOccasion(occasion)}
                  className={`occasion-card ${
                    selectedOccasion === occasion ? "selected" : ""
                  }`}
                >
                  {occasion}
                </button>
              ))}
            </div>
          </div>
        );

      case "dietary":
        return (
          <div className="dietary-search">
            <p className="search-description">
              Select dietary preferences to find suitable pairings
            </p>
            <div className="dietary-options">
              {dietaryOptions.map((option) => (
                <label key={option} className="dietary-option">
                  <input
                    type="checkbox"
                    checked={dietaryRestrictions.includes(option)}
                    onChange={() => toggleDietaryRestriction(option)}
                  />
                  <span className="dietary-label">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderSuggestions = () => {
    if (loading) {
      return (
        <div className="suggestions-loading">
          <div className="loading-spinner"></div>
          <p>Finding perfect pairings...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="suggestions-error">
          <p className="error-message">{error}</p>
        </div>
      );
    }

    if (suggestions.length === 0) {
      return (
        <div className="no-suggestions">
          <p>
            No suggestions yet. Use the search above to find perfect pairings!
          </p>
        </div>
      );
    }

    return (
      <div className="suggestions-grid">
        {suggestions.map((suggestion) => (
          <button
            key={
              suggestion.id ||
              suggestion.beverageId ||
              suggestion.name ||
              Math.random()
            }
            className="suggestion-card"
            onClick={() => handleSuggestionSelect(suggestion)}
            type="button"
          >
            {suggestion.beverage ? (
              <>
                <div className="suggestion-header">
                  <h4 className="beverage-name">{suggestion.beverage.name}</h4>
                  <span className="beverage-type">
                    {suggestion.beverage.type}
                  </span>
                </div>

                <div className="suggestion-metrics">
                  <div className="confidence-indicator">
                    <span className="metric-label">Match:</span>
                    <div className="confidence-bar">
                      <div
                        className="confidence-fill"
                        style={{
                          width: `${
                            (suggestion.confidence ||
                              suggestion.combinedScore) * 100
                          }%`,
                        }}
                      />
                    </div>
                    <span className="confidence-value">
                      {(
                        (suggestion.confidence || suggestion.combinedScore) *
                        100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>

                  {suggestion.scienceScore && (
                    <div
                      className={`science-indicator ${suggestion.colorClass}`}
                    >
                      <span className="science-score">
                        {suggestion.scienceScore}
                      </span>
                    </div>
                  )}
                </div>

                <p className="suggestion-reason">
                  {suggestion.reason ||
                    suggestion.explanation ||
                    suggestion.description}
                </p>

                {suggestion.foodSuggestion && (
                  <div className="food-suggestion">
                    <span className="food-label">Perfect with:</span>
                    <span className="food-name">
                      {suggestion.foodSuggestion}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="generic-suggestion">
                <h4>{suggestion.title || suggestion.name}</h4>
                <p>{suggestion.description || suggestion.reason}</p>
              </div>
            )}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="pairing-explorer">
      <div className="explorer-header">
        <h2>🔍 Find Perfect Pairings</h2>
        <p className="explorer-subtitle">
          Discover the ideal beverage for any food, occasion, or dietary
          preference
        </p>
      </div>

      <div className="search-mode-tabs">
        <button
          onClick={() => setSearchMode("food")}
          className={`mode-tab ${searchMode === "food" ? "active" : ""}`}
        >
          🍽️ By Food
        </button>
        <button
          onClick={() => setSearchMode("occasion")}
          className={`mode-tab ${searchMode === "occasion" ? "active" : ""}`}
        >
          🎉 By Occasion
        </button>
        <button
          onClick={() => setSearchMode("dietary")}
          className={`mode-tab ${searchMode === "dietary" ? "active" : ""}`}
        >
          🥗 Dietary Preferences
        </button>
      </div>

      <div className="search-interface">{renderSearchInterface()}</div>

      <div className="suggestions-section">
        <h3 className="suggestions-title">Recommendations</h3>
        {renderSuggestions()}
      </div>
    </div>
  );
};

PairingExplorer.propTypes = {
  restaurantId: PropTypes.string.isRequired,
  beverages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string,
      category: PropTypes.string,
    })
  ),
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      category: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  onPairingFound: PropTypes.func,
};

PairingExplorer.defaultProps = {
  beverages: [],
  menuItems: [],
  onPairingFound: null,
};

export default PairingExplorer;
